export type TarotOrientation = 'upright' | 'reversed';
export type TarotSelector = 'customer' | 'reader';

export interface TarotCard {
  key: string;
  name: string;
  file: string;
}

export interface TarotPlacedCard {
  cardKey: string;
  positionKey: string;
  orientation: TarotOrientation;
  selectedBy: TarotSelector;
}

export interface TarotDraw {
  version: 1;
  deckKey: 'rws-1909';
  spreadKey: string;
  delegateToReader: boolean;
  shuffleCount: number;
  cards: TarotPlacedCard[];
  actions?: Array<{ action:'draw'|'move'|'remove'|'shuffle'; cardKey?:string; from?:string; to?:string; by:TarotSelector; at:string }>;
}

export interface TarotSpreadPosition { key: string; label: string; x: number; y: number; rotate?: number; }
export interface TarotSpread { key: string; title: string; description: string; positions: TarotSpreadPosition[]; }

const majorNames = [
  'Deli','Büyücü','Başrahibe','İmparatoriçe','İmparator','Aziz','Âşıklar','Savaş Arabası','Güç','Ermiş',
  'Kader Çarkı','Adalet','Asılan Adam','Ölüm','Denge','Şeytan','Kule','Yıldız','Ay','Güneş','Mahkeme','Dünya'
];
const majorFiles = [
  '00_Fool','01_Magician','02_High_Priestess','03_Empress','04_Emperor','05_Hierophant','06_Lovers','07_Chariot','08_Strength','09_Hermit',
  '10_Wheel_of_Fortune','11_Justice','12_Hanged_Man','13_Death','14_Temperance','15_Devil','16_Tower','17_Star','18_Moon','19_Sun','20_Judgement','21_World'
];
const ranks = ['As','İki','Üç','Dört','Beş','Altı','Yedi','Sekiz','Dokuz','On','Uşak','Şövalye','Kraliçe','Kral'];
const suits = [
  { key:'cups', title:'Kupa', file:'Cups' },
  { key:'pentacles', title:'Tılsım', file:'Pents' },
  { key:'swords', title:'Kılıç', file:'Swords' },
  { key:'wands', title:'Değnek', file:'Wands' }
];

export const TAROT_CARDS: TarotCard[] = [
  ...majorNames.map((name,index)=>({key:`major-${String(index).padStart(2,'0')}`,name,file:`${majorFiles[index]}.webp`})),
  ...suits.flatMap(suit=>ranks.map((rank,index)=>({key:`${suit.key}-${String(index+1).padStart(2,'0')}`,name:`${suit.title} ${rank}`,file:`${suit.file}${String(index+1).padStart(2,'0')}.webp`})))
];

const line = (labels:string[], y=50):TarotSpreadPosition[] => labels.map((label,index)=>({
  key:`p${index+1}`, label, x:labels.length===1?50:12+(76*index/(labels.length-1)), y
}));

export const TAROT_SPREADS: TarotSpread[] = [
  {key:'single',title:'Tek Kart',description:'Tek bir konu için kısa odak.',positions:line(['Odak'])},
  {key:'past-present-future',title:'Geçmiş · Şimdi · Gelecek',description:'Konunun zaman içindeki üç yönü.',positions:line(['Geçmiş','Şimdi','Gelecek'])},
  {key:'situation-obstacle-advice',title:'Durum · Engel · Öneri',description:'Durumu, zorlayan tarafı ve izlenebilecek yolu gösterir.',positions:line(['Durum','Engel','Öneri'])},
  {key:'relationship-five',title:'İlişki Açılımı',description:'İki tarafı ve aradaki bağı beş konumda ele alır.',positions:[{key:'p1',label:'Sen',x:15,y:28},{key:'p2',label:'Karşı taraf',x:85,y:28},{key:'p3',label:'Bağ',x:50,y:50},{key:'p4',label:'Zorluk',x:25,y:78},{key:'p5',label:'Yol',x:75,y:78}]},
  {key:'decision-five',title:'Karar Açılımı',description:'İki seçeneği ve kararın merkezini karşılaştırır.',positions:[{key:'p1',label:'Kararın özü',x:50,y:18},{key:'p2',label:'Birinci yol',x:18,y:48},{key:'p3',label:'İkinci yol',x:82,y:48},{key:'p4',label:'Dikkat',x:28,y:82},{key:'p5',label:'Öneri',x:72,y:82}]},
  {key:'horseshoe-seven',title:'At Nalı',description:'Geçmişten sonuca uzanan yedi konumlu açılım.',positions:[{key:'p1',label:'Geçmiş',x:8,y:68},{key:'p2',label:'Şimdi',x:18,y:38},{key:'p3',label:'Gizli etki',x:34,y:18},{key:'p4',label:'Merkez',x:50,y:10},{key:'p5',label:'Dış etki',x:66,y:18},{key:'p6',label:'Öneri',x:82,y:38},{key:'p7',label:'Sonuç',x:92,y:68}]},
  {key:'path-seven',title:'Yol Açılımı',description:'Hedefe giden yolu ve dönüm noktalarını gösterir.',positions:[{key:'p1',label:'Başlangıç',x:10,y:75},{key:'p2',label:'İlk adım',x:24,y:55},{key:'p3',label:'Kaynak',x:38,y:75},{key:'p4',label:'Dönüm noktası',x:52,y:45},{key:'p5',label:'Bırakılacak',x:66,y:70},{key:'p6',label:'Kazanılacak',x:80,y:45},{key:'p7',label:'Yön',x:92,y:20}]},
  {key:'celtic-cross',title:'Kelt Haçı',description:'Konuyu çevresi ve olası yönüyle ayrıntılı ele alan klasik açılım.',positions:[{key:'p1',label:'Şimdi',x:26,y:50},{key:'p2',label:'Etkileyen',x:38,y:50},{key:'p3',label:'Temel',x:32,y:73},{key:'p4',label:'Geçmiş',x:10,y:50},{key:'p5',label:'Olasılık',x:32,y:18},{key:'p6',label:'Yakın dönem',x:53,y:50},{key:'p7',label:'Tutum',x:78,y:73},{key:'p8',label:'Çevre',x:78,y:55},{key:'p9',label:'Umut ve kaygı',x:78,y:37},{key:'p10',label:'Yön',x:78,y:19}]}
];

export const TAROT_ASSET_ROOT='/assets/tarot/rws-1909';
export const tarotCard=(key:string)=>TAROT_CARDS.find(card=>card.key===key);
export function parseTarotDraw(value:unknown):TarotDraw|null{
  let candidate=value;
  if(typeof candidate==='string'){try{candidate=JSON.parse(candidate);}catch{return null;}}
  if(!candidate||typeof candidate!=='object'||Array.isArray(candidate))return null;
  const raw=candidate as Record<string,unknown>;
  if(raw['version']!==1||raw['deckKey']!=='rws-1909'||typeof raw['spreadKey']!=='string')return null;
  const validKeys=new Set(TAROT_CARDS.map(card=>card.key));
  const cards=Array.isArray(raw['cards'])?raw['cards'].filter((item):item is TarotPlacedCard=>{
    if(!item||typeof item!=='object')return false;
    const card=item as unknown as Record<string,unknown>;
    return validKeys.has(String(card['cardKey']||''))&&typeof card['positionKey']==='string';
  }).slice(0,15).map(card=>({...card,orientation:card.orientation==='reversed'?'reversed':'upright',selectedBy:card.selectedBy==='reader'?'reader':'customer'} as TarotPlacedCard)):[];
  const actions=Array.isArray(raw['actions'])?raw['actions'].filter(item=>item&&typeof item==='object').slice(-60) as TarotDraw['actions']:[];
  return {version:1,deckKey:'rws-1909',spreadKey:String(raw['spreadKey']),delegateToReader:!!raw['delegateToReader'],shuffleCount:Math.max(0,Number(raw['shuffleCount']||0)),cards,actions};
}
